import React, { useState, useEffect, useRef } from 'react';
import { API } from "aws-amplify";
import Fuse from 'fuse.js';

import DepartmentProgressScreen from '../department_progress_screen';
import EditTeacherData from '../../modals/edit_teacher';
import SupervisorSelector from '../reusable-components/SupervisorSelector';
import SupervisorPill from '../reusable-components/SupervisorPill';
import Button from '../../../Button';
import SearchAddButton from '../../../SearchAddButton';
import useDepartmentResourceFeedback from './useDepartmentResourceFeedback';

import { isEmpty, useOutsideClick, getData, calculateConflicts } from '../../../../js/Helpers';
import { getCoursePeriodIdForPeriod } from '../../../../js/PeriodHelpers';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faUserCircle, faSquare, faCheckSquare, faHourglassHalf, faCheckCircle, faCheck, faExclamationCircle, faTimes, faSearch, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { CONFLICT_TYPES } from '../../../../js/Constants';
import { filterOutConflicts } from '../../../../js/Conflicts';
import { useConflictActions, useConflictData } from '../../store/magnetboardStore';

export default function CleanTeacherAvailability({isLoading, subscreenRestricted, subscreenLocked, subscreen, subscreenResponsibleRoleID, supervisors, openSupervisorsList, closeSupervisorsList, addSupervisorToDepartment, removeSupervisorFromDepartment, users, userIsSupervisor, userIsAdmin, departments, setDepartments, departmentID, sortedDepartments, changeDepartment, userInfo, schoolInfo, schoolType, scheduleInfo, teachersLoading, teachers, setTeachers, courses, classrooms, students, sections, scheduleStructureData, scheduleType, periods, addNewTeacherOpen, setAddNewTeacherOpen, subscreenSelect, submitDepartment, reviewing, changeDepartmentResource, setRolePages, showNextButton, handleMoveToNextScreen, setScreenAllowsUniversalSidebar, setUniversalSidebarLoadingInfo}) {
	const ref = useRef();

	const [departmentTeachers, setDepartmentTeachers] = useState(null);
	const [selectedPeriods, setSelectedPeriods] = useState([]);
	const [addTeacherOpen, setAddTeacherOpen] = useState(false);
	const [teacherResults, setTeacherResults] = useState([...teachers]);
	const [changesSavedMessage, setChangesSavedMessage] = useState(false);
	const { departmentResourceAddLoading, isDepartmentResourcePendingRemoval, startDepartmentResourceFeedback, clearDepartmentResourceFeedback } = useDepartmentResourceFeedback({ items: departmentTeachers || [], dataIdKey:'teacher_id', dataAttribute:'data-teacherid' });

	const { setConflicts, markConflictDataFresh } = useConflictActions();
	const { conflicts } = useConflictData();

	const addSearch = (e) => {
		const search_value = e.target.value;
		if(search_value.trim().length === 0)
		{
			setTeacherResults([...teachers]);
			return;
		}

		const fuse = new Fuse(teachers, {
			keys: ['name', 'first_name'],
			threshold: .6
		})

		const results = fuse.search(search_value);
		const teacher_results = results.map(result => result.item);

		setTeacherResults(teacher_results);
	}

	useEffect(() => {
		if(addTeacherOpen) setTeacherResults([...teachers]);
	}, [addTeacherOpen, teachers]);

	const toggleSelectAllColumn = (period_id) => {
		const course_period_id = getCoursePeriodIdForPeriod(scheduleStructureData, period_id, {
			schedule_type: scheduleInfo.schedule_type,
			current_day: '1',
		});
		if(!course_period_id) return;
		const period_selected_index = selectedPeriods.findIndex(period => period === period_id);
		let delete_restriction;

		if(period_selected_index === -1)
		{
			selectedPeriods.push(period_id);
			delete_restriction = '0';
		}
		else
		{
			selectedPeriods.splice(period_selected_index, 1);
			delete_restriction = '1';
		}

		setSelectedPeriods([...selectedPeriods]);

		let teacher_ids = [];
		(departmentTeachers || []).forEach(teacher_info => {
			teacher_ids.push(teacher_info.teacher_id);
		});

		updateTeacherAvailability(teacher_ids, delete_restriction, [course_period_id]);
	}

	const toggleSelectAllRow = (teacher_id) => {
		const teacher_info = teachers.find(teacher => teacher.teacher_id === teacher_id);
		const period_restrictions = teacher_info.period_restrictions;
		const delete_restriction = (period_restrictions.length === 0) ? '0' : '1';

		const course_period_ids = periods.map(period => {
			const period_id = period.period_id;
			const course_period_id = getCoursePeriodIdForPeriod(scheduleStructureData, period_id, {
				schedule_type: scheduleInfo.schedule_type,
				current_day: '1',
			});
			return course_period_id;
		}).filter(course_period_id => !!course_period_id);

		if(delete_restriction === '1') setSelectedPeriods([]);

		updateTeacherAvailability([teacher_id], delete_restriction, course_period_ids);
	}

	////////////////////////////
	///// UPDATE FUNCTIONS /////
	////////////////////////////
	const updateTeacherAvailability = async (teacher_ids, delete_restriction, course_period_ids) => {
		///////////////////////
		///// UPDATE JSON /////
		///////////////////////
		const department_index = departments.findIndex(department => department.department_id === departmentID);
		const department_subscreen = departments[department_index]?.subscreens?.[subscreen];

		if(userIsSupervisor)
		{
			if(department_subscreen)
			{
				department_subscreen.submitted = false;
				department_subscreen.confirmed = false;
			}

			// Reset overall progress to update the Progress screen
			getData('schoolProgress', '/get-school-progress', {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id}, true).then(user_progress_data => {
				const role_pages = user_progress_data.role_pages;
				setRolePages(role_pages);
			});
		}

		if(userIsAdmin && department_subscreen?.confirmed) setChangesSavedMessage(true);

		teacher_ids.forEach(teacher_id => {
			const teacher_index = teachers.findIndex(teacher => teacher.teacher_id === teacher_id);
			const teacher_info = teachers[teacher_index];
			const period_restrictions = teacher_info.period_restrictions;

			if(delete_restriction === '1')
			{
				const new_period_restrictions = period_restrictions.filter(period => !course_period_ids.includes(period));
				teacher_info.period_restrictions = new_period_restrictions;
			}
			else if(delete_restriction === '0')
			{
				// Check if teacher already has period restriction
				// If no, add period
				course_period_ids.forEach(course_period_id => {
					const period_index = period_restrictions.findIndex(period => period === course_period_id);

					if(period_index === -1) period_restrictions.push(course_period_id);
				});
			}
		});

		setTeachers([...teachers]);
		setDepartments([...departments]);

		//////////////////////////
		///// UPDATE BACKEND /////
		//////////////////////////
		const data = {school_id: schoolInfo.school_id, department_id:departmentID, teacher_ids:teacher_ids, delete_restriction:delete_restriction, course_period_ids:course_period_ids, schedule_version_id:schoolInfo.current_schedule_version_id};

		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/clean-data/update-teacher-availability';
	    const myInit = { // OPTIONAL
	        response: true,
	        body: JSON.stringify(data),
	    };

		try {
			API.post(apiName, url, myInit);

			////////////////////////
			/// Update conflicts ///
			////////////////////////
			const affected_sections = sections.reduce((results, section) => {
				const section_teacher_id = section.teacher_id;
				if(section_teacher_id && teacher_ids.includes(section_teacher_id)) results.push(section.section_id);
				return results;
			}, []);
			const conflicts_to_check = [CONFLICT_TYPES.TEACHER_PERIOD_RESTRICTION];

			if(affected_sections.length > 0) calculateConflicts({school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id,  conflicts_to_check:conflicts_to_check}).then((conflicts_response) => {
				const conflicts_list = conflicts_response.conflicts;
				const filtered_out_conflicts = filterOutConflicts(conflicts, [], conflicts_to_check);
				setConflicts([...filtered_out_conflicts, ...conflicts_list]);
				markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id);
			});
		} catch(e)
		{
			console.log(e.response);
		}
	}

	const updateTeacher = async (changeType, dataType, data_id, old_department_id, new_department_id) => {
		const changed = await changeDepartmentResource(changeType, dataType, data_id, old_department_id, new_department_id, () => {
			if(changeType == 'add' && userIsSupervisor)
			{
				const department_index = departments.findIndex(department => department.department_id === departmentID);
				const department_subscreens = departments[department_index]?.subscreens;

				if(department_subscreens && department_subscreens[subscreen])
				{
					department_subscreens[subscreen].submitted = false;
					department_subscreens[subscreen].confirmed = false;
				}

				// Update cleaning screen progress (for check marks)
				if(department_subscreens && department_subscreens['teacher_availability'])
				{
					department_subscreens['teacher_availability'].submitted = false;
					department_subscreens['teacher_availability'].confirmed = false;
				}

				if(department_subscreens && department_subscreens['course_teachers'])
				{
					department_subscreens['course_teachers'].submitted = false;
					department_subscreens['course_teachers'].confirmed = false;
				}

				if(department_subscreens && department_subscreens['teacher_classrooms'])
				{
					department_subscreens['teacher_classrooms'].submitted = false;
					department_subscreens['teacher_classrooms'].confirmed = false;
				}

				setDepartments([...departments]);

				// Reset overall progress to update the Progress screen
				getData('schoolProgress', '/get-school-progress', {school_id: schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id}, true).then(user_progress_data => {
					const role_pages = user_progress_data.role_pages;

					setRolePages(role_pages);
				});
			}
		}, {
			onStart: () => startDepartmentResourceFeedback(changeType, data_id),
			onError: clearDepartmentResourceFeedback,
		});

		if(!changed) clearDepartmentResourceFeedback();
	}

	useOutsideClick(ref, () => {
		closeSupervisorsList();
	});

	//////////////////////////////
	//// USE EFFECT FUNCTIONS ////
	//////////////////////////////
	useEffect(() => {
		if(!departmentID || !userInfo || !users || !departments) return;

		const department_index = departments.findIndex(department => department.department_id === departmentID);
		const department_info = (department_index !== -1) ? departments[department_index] : null;
		const department_supervisors = (department_info) ? department_info.supervisors : [];

		const available_users = users.filter(user => (user.role_id === '1' || user.role_id === '2') || department_supervisors.findIndex(supervisor => supervisor.user_id === user.user_id) !== -1);

		setScreenAllowsUniversalSidebar(true);
		setUniversalSidebarLoadingInfo({sidebar_name:"Teacher Availability Notes", sidebar_type:'notes', subscreen:'teacher_availability', department_id:departmentID, user_id:userInfo.user_id, available_users:available_users, help_link_path:`/app/${schoolType}/documentation/scheduling_wizard/edit_constraints/setting_constraints`});
	}, [departmentID, userInfo, users, departments, setScreenAllowsUniversalSidebar, setUniversalSidebarLoadingInfo]);

	useEffect(() => {
		if(teachersLoading)
		{
			setDepartmentTeachers(null);
			return;
		}

		const department_teachers = (departmentID) ? teachers.filter(teacher => teacher.departments.includes(departmentID)) : [];
		setDepartmentTeachers([...department_teachers]);
	}, [departmentID, teachersLoading, teachers]);

	useEffect(() => {
		if(Array.isArray(departmentTeachers) && departmentTeachers.length > 0 && selectedPeriods.length === 0)
		{
				let selected_periods = [];
				periods.forEach(period_info => {
					const period_id = period_info.period_id;
					const course_period_id = getCoursePeriodIdForPeriod(scheduleStructureData, period_id, {
						schedule_type: scheduleInfo.schedule_type,
						current_day: '1',
					});
					if(!course_period_id) return;

					const period_restricted = teachers.findIndex(teacher => departmentTeachers.findIndex(department_teacher => department_teacher.teacher_id === teacher.teacher_id) !== -1 && teacher.period_restrictions.includes(course_period_id)) !== -1;

				if(period_restricted && !selected_periods.includes(period_id)) selected_periods.push(period_id);
			});

			setSelectedPeriods(selected_periods);
		}
	}, [departmentTeachers, teachers, periods, scheduleInfo.schedule_type, scheduleStructureData.schedule_structure, setSelectedPeriods]);

	const num_periods = (!isEmpty(periods)) ? Object.keys(periods).length : 0;
	const row_style = {gridTemplateColumns:`200px repeat(${num_periods}, 1fr) 40px`};

	const department_index = departments.findIndex(department => department.department_id === departmentID);
	const department_info = departments[department_index];
	const subscreen_info = department_info?.subscreens?.[subscreen] || null;

	const current_screen_submitted = (subscreen_info) ? subscreen_info.submitted : false;
	const current_screen_confirmed = (subscreen_info) ? subscreen_info.confirmed : false;
	const current_screen_unlocked= (subscreen_info) ? subscreen_info.unlocked : false;

	const show_current_screen = (current_screen_unlocked && (reviewing || userIsSupervisor || userIsAdmin));
	const screen_complete_for_user = ((current_screen_confirmed) || (userIsSupervisor && !userIsAdmin && current_screen_submitted));
	const department_teachers = departmentTeachers || [];
	const department_teachers_ready = departmentTeachers !== null;

	return (
		<>
		<h1 className='progress-main-header'>Teacher Availability</h1>
		<p className='progress-subheader'>Set any constraints on what periods teachers can and cannot teach.</p>
		{(subscreenRestricted) ?
		(
			<div className='general-screen-message'>
				<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
				<h1>No Access</h1>
				<div>You do not have access to this screen</div>
			</div>
		):(subscreenLocked) ?
		(
			<div className='general-screen-message'>
				<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
				<h1>Temporarily Locked</h1>
				<div>This screen is locked until all other prior steps are completed</div>
			</div>
		):(!departmentID) ?
		(
			<DepartmentProgressScreen subscreenResponsibleRoleID={subscreenResponsibleRoleID} subscreen={subscreen} sortedDepartments={sortedDepartments} changeDepartment={changeDepartment} subscreenSelect={subscreenSelect} userInfo={userInfo} userIsAdmin={userIsAdmin} userIsSupervisor={userIsSupervisor} />
		):(!userIsSupervisor) ?
		(
			<div className='general-screen-message'>
				<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
				<h1>No Access</h1>
				<div>You do not have access to change this department</div>
				<div className='blue-link' style={{marginTop:'10px'}} onClick={() => changeDepartment(null)}>Go back to department list</div>
			</div>
		):(departmentID) ?
		(
			<>
			<div id='clean-data-content'>
				<div>
					<FontAwesomeIcon icon={faChevronLeft} className='progress-back-to-departments-btn' onClick={()=>changeDepartment(null)}/>
				</div>
				<div className='clean-data-content-inner-content'>
					<div className='clean-data-submit-btn-container'>
						{!isLoading &&
							<>
							{(reviewing && screen_complete_for_user) &&
								<>
								{changesSavedMessage &&
									<div className='all-changes-saved-message' style={{margin:'15px 0px 0px 0px'}}>
										<FontAwesomeIcon className='green-text' icon={faCheckCircle}/>
										<div>
											<h5>Changes Saved</h5>
											<div className='small-text'>All your changes have been saved</div>
										</div>
										<FontAwesomeIcon className='x-remove' icon={faTimes} onClick={() => setChangesSavedMessage(false)}/>
									</div>
								}
								</>
							}
							{(!screen_complete_for_user && show_current_screen) &&
								<Button key={departmentID} classes={['mark-as-complete-btn']} selected={false} text={`${!current_screen_submitted ? 'Mark As Complete' : 'Confirm Department'}`} icon={faCheck} iconPosition='left' onClickFunction={() => submitDepartment(departmentID)} />
							}
							</>
						}
					</div>
					<div className='clean-data-content-header-container'>
						<h1 className='clean-data-content-header'>{department_info && department_info.department} Department</h1>
						{current_screen_confirmed ?
							(
								<div className='clean-data-status green-text'><div>Complete</div><FontAwesomeIcon icon={faCheckCircle}/></div>
							): (current_screen_submitted) ?
							(
								<div className='clean-data-status bright-yellow-text'><div>Pending Confirmation</div><FontAwesomeIcon icon={faCheckCircle}/></div>
							): (null)
						}
					</div>
					<div className='department-supervisor-parent-container'>
						<>
						{department_info && department_info.supervisors.length === 0 &&
							<>
							<div className='department-supervisor-inner-container'>
								<FontAwesomeIcon className='department-supervisor-user-icon' icon={faUserCircle}/>
								<div className='department-supervisor-name'>No Supervisor Assigned</div>
							</div>
							</>
						}
						{department_info && department_info.supervisors.map((supervisor, index) => {
							return (
								<SupervisorPill
									supervisor={supervisor}
									canRemove={userIsAdmin}
									onRemove={() => removeSupervisorFromDepartment(supervisor.user_id)}
									key={index}
								/>
							)
						})}
						{userIsAdmin &&
							<>
							<SupervisorSelector
								triggerClassName='clean-data-add-supervisor-link small-text click-restricted'
								triggerText='Add a Supervisor'
								isEmpty={department_info && department_info.supervisors.length === 0}
								onOpen={() => openSupervisorsList(departmentID)}
								isOpen={department_info && department_info.supervisors_open}
								optionsRef={ref}
								supervisors={supervisors}
								onAddSupervisor={supervisor_info => addSupervisorToDepartment(supervisor_info)}
								onClose={() => closeSupervisorsList(departmentID)}
								settingsPath={`/app/${schoolType}/settings/users`}
								redirectPath={`/app/${schoolType}/progress/edit_constraints/teacher_availability`}
							/>
							</>
						}
						</>
					</div>
					<div className='clean-data-container'>
						{isLoading ?
							(
								<div className="center-text">
									<img className='clean-data-loader' src={require(`../../../../images/balls.gif`)} alt='loading' />
								</div>
							) :
							(
								<>
								{(!current_screen_unlocked) ?
									(
										<div className='general-screen-message'>
											<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
											<h1>Locked</h1>
											<div>This department has previous steps which have not been completed. Please review previous steps and make sure they are completed and saved. This page should open automatically once all previous steps have been finished.</div>
										</div>
									):(userIsAdmin && current_screen_unlocked && !show_current_screen && !current_screen_submitted && !userIsSupervisor) ?
									(
										<div className='general-screen-message'>
											<FontAwesomeIcon className='turquoise-text' style={{fontSize:'60px'}} icon={faHourglassHalf}/>
											<h1>Pending...</h1>
											<div>Waiting on confirmation from department supervisor</div>
											<p className='blue-link'>Review teacher availability yourself</p>
										</div>
									):(show_current_screen) ?
									(
										<>
										{!department_teachers_ready ?
											(
												<div className='center-text'>
													<img className='clean-data-loader' src={require(`../../../../images/balls.gif`)} alt='loading' />
												</div>
											) :
											department_teachers.length === 0 ?
											(
												<div className='clean-data-no-data-container'>
													<FontAwesomeIcon className='clean-data-no-data-icon dark-blue-text' icon={faExclamationCircle}/>
													<div>No teachers found</div>
													<p>Please confirm that this is correct by submitting the screen</p>
												</div>
											):
											(
												<>
												<div className='fixed-heading-on-scroll'>
													<div className='clean-data-header-row' style={{gridTemplateColumns:'200px 1fr 40px', margin:'0px 0px 5px 0px'}}>
														<div></div>
														<div className='dark-blue' style={{textAlign:'center', padding:'5px', borderRadius:'5px'}}>Available Periods</div>
														<div></div>
													</div>
													<div className='clean-data-header-row' style={row_style}>
														<div className='clean-data-col'></div>
														{periods.map(period_info => {
															const period_id = period_info.period_id;

															return (
																<div className="clean-data-col clean-data-period" key={`header-${period_id}`}>
																	<div className="clean-data-heading extra-small-text">{period_info.period_name}</div>
																</div>
															)
														})}
													</div>
												</div>
												<div className='clean-data-select-row' style={row_style}>
													<div className='clean-data-col'></div>
													{Object.keys(periods).map(index => {
														const period_info = periods[index];
														const period_id = period_info.period_id;
														const select_state = selectedPeriods.includes(period_id) ? 'Select' : 'Unselect';

														if(!period_info) return null;

														return (
															<div className="clean-data-col extra-small-text select-all text-align-center" key={index} onClick={() => toggleSelectAllColumn( period_id)}><span className='capitalize'>{select_state}</span> All</div>
														)
													})}
												</div>
												{department_teachers.map((teacher_info, i) => {
													const teacher_id = teacher_info.teacher_id;
													const period_restrictions = teacher_info.period_restrictions;
													const dual_dept_bool = ([...new Set(teacher_info.departments)].length > 1) ? true : false;
													const removing_teacher = isDepartmentResourcePendingRemoval(teacher_id);

													return (
														<div className='clean-data-row' style={row_style} key={i} data-teacherid={teacher_id}>
															{removing_teacher ?
																<div className='center-text' style={{gridColumn:'1 / -1', padding:'10px 0px'}}>
																	<img className='clean-data-loader' src={require(`../../../../images/balls.gif`)} alt='loading' />
																</div>
																:
																<>
																	<div>
																		<div>{teacher_info.name}, {teacher_info.first_name}</div>
																		{dual_dept_bool &&
																			<div className='orange-text small-text'>Dual Depts.</div>
																		}
																		{period_restrictions.length > 0 ?
																			(
																				<div className='small-text approve-link' onClick={() => toggleSelectAllRow(teacher_id)}>Select All</div>
																			):
																			(
																				<div className='small-text cancel-link' onClick={() => toggleSelectAllRow(teacher_id)}>Unselect All</div>
																			)
																		}
																	</div>
																	{periods.map(period_info => {
																		const period_id = period_info.period_id;
																		const course_period_id = getCoursePeriodIdForPeriod(scheduleStructureData, period_id, {
																			schedule_type: scheduleInfo.schedule_type,
																			current_day: '1',
																		});
																		if(!course_period_id) {
																			return (
																				<div key={`teacher-period-${period_id}`} className='period-availability-checkmark'></div>
																			);
																		}

																		const period_restricted = period_restrictions.includes(course_period_id);

																		return (
																			<div key={`teacher-period-${period_id}`} className='period-availability-checkmark'>
																				{period_restricted ?
																					(
																						<FontAwesomeIcon className='fas-checkbox-unchecked' icon={faSquare} onClick={() => updateTeacherAvailability([teacher_id], '1', [course_period_id])}/>
																					) :
																					(
																						<FontAwesomeIcon className='fas-checkbox-checked' icon={faCheckSquare} onClick={() => updateTeacherAvailability([teacher_id], '0', [course_period_id])}/>
																					)
																				}
																			</div>
																		)
																	})}
																	<FontAwesomeIcon className='gray-to-red-link' icon={faTimes} onClick={() => updateTeacher('remove', 'teacher', teacher_id, departmentID, null)} style={{justifySelf:'center'}}/>
																</>
															}
														</div>
													)
												})}
												</>
											)
										}
										{departmentResourceAddLoading &&
											<div className='center-text' style={{padding:'10px 0px 0px 0px'}}>
												<img className='clean-data-loader' src={require(`../../../../images/balls.gif`)} alt='loading' />
											</div>
										}
										<div className='clean-data-add-new-data blue-link assign-resources-supervisor-confirm-add-new' onClick={() => setAddTeacherOpen(!addTeacherOpen)}>Add A Teacher</div>
										{addTeacherOpen &&
											<div className='search-add-row-container'>
												<div className='search-add-search-container'>
													<div className='search-add-adding-input-container'>
														<input id='teacher-search-input' className='search-add-adding-input' onChange={(e) => addSearch(e)} placeholder='Search for teacher'/>
														<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
													</div>
													<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes} onClick={() => setAddTeacherOpen(!addTeacherOpen)}/>
												</div>
												{teacherResults.length !== 0 &&
													<div className='search-add-search-results'>
														<div className='search-add-did-you-mean'>Did you mean:</div>
														{teacherResults.map((teacher, i) => {
															const teacher_departments = teacher.departments.reduce((result, department_id) => {
																const department_index = departments.findIndex(department => department.department_id === department_id);

																if(department_index !== -1) result.push(departments[department_index].department);

																return result;
															}, []);

															const department_text = teacher_departments.join(', ');
															const teacher_selected = teacher.departments.includes(departmentID);

															return (
																<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'1fr 1fr auto'}}>
																	<div>{teacher.name}, {teacher.first_name}</div>
																	<div className='dark-gray-text small-text'>{department_text}</div>
																	<div>
																		<SearchAddButton selected={teacher_selected} onAdd={() => updateTeacher('add', 'teacher', teacher.teacher_id, null, departmentID)} />
																	</div>
																</div>
															)
														})}
														<div className='clean-data-add-new-data-text dark-blue-text'>If you don&rsquo;t see the teacher you are looking for, you should first add them <span className='blue-link' onClick={() => setAddNewTeacherOpen(true)}>Add A New Teacher</span></div>
													</div>
												}
											</div>
										}
										{addNewTeacherOpen &&
											<EditTeacherData schoolInfo={schoolInfo} dataID={null} toggleEditData={() => setAddNewTeacherOpen(false)} scheduleStructureData={scheduleStructureData} scheduleInfo={scheduleInfo} teachers={teachers} students={students} courses={courses} classrooms={classrooms} sections={sections} departments={departments} setDatabaseData={setTeachers} defaultDepartmentID={departmentID}/>
										}
										</>
									):null
								}
								</>
							)
						}
					</div>
				</div>
			</div>
			</>
		):null
		}
		{showNextButton &&
			<div className='fixed-bottom-save-container'>
				<Button classes={['btn', 'btn-extra-large', 'green-btn', 'align-right', 'move-to-next-screen-btn']} selected={false} text='Go to Next Step: Course Availability' onClickFunction={handleMoveToNextScreen} />
			</div>
		}
		</>
	);
}
